import * as _ from 'lodash';
import { action, autorun, IObservableArray, makeObservable, observable } from 'mobx';
import { BaseObserver, BaseListener } from '../utils/BaseObserver';

export interface SearchResultEntry {
  key: string;
}

export interface SearchResultListener extends BaseListener {
  /**
   * Called when the search results are disposed of
   */
  dispose: () => any;
}

export class SearchResult<T extends SearchResultEntry> extends BaseObserver<SearchResultListener> {
  @observable
  loading: boolean;

  @observable
  results: IObservableArray<T>;

  constructor() {
    super();
    this.loading = true;
    this.results = [] as unknown as IObservableArray<T>;
    makeObservable(this);
  }

  @action setValues(results: T[]) {
    this.results.replace(results);
    this.loading = false;
  }

  dispose() {
    this.iterateListeners((listener) => {
      listener.dispose && listener.dispose();
    });
  }

  pipe<S extends SearchResultEntry>(
    result: SearchResult<S>,
    transform: (res: IObservableArray<T>) => S[],
    additionalAutorun?: (source: this) => any
  ) {
    const dispose = autorun(() => {
      if (!this.loading) {
        result.setValues(transform(this.results));
      }
      result.loading = this.loading;
      if (additionalAutorun) {
        additionalAutorun(this);
      }
    });
    result.registerListener({
      dispose: () => {
        this.dispose();
        dispose();
      }
    });
  }
}

/**
 * Fast search term generator that trims and null checks
 */
export const trimSearch = _.memoize((a: string): string | null => {
  if (a == null) {
    return null;
  }
  const s = a.trim().toLowerCase();
  if (s === '') {
    return null;
  }
  return s;
});

/**
 * Fast searcher that checks two strings and caters for null
 */
const searcher = _.memoize((options: { search: string; entity: string; nullIsTrue?: boolean }): SearchEventMatch => {
  const searchTrimmed = trimSearch(options.search);

  if (options.nullIsTrue == null) {
    options.nullIsTrue = true;
  }

  // null is a bit more tricky
  if (searchTrimmed === null) {
    if (options.nullIsTrue) {
      return {
        locators: []
      };
    }
    return false;
  }
  const pos = options.entity.toLowerCase().indexOf(searchTrimmed);
  if (pos !== -1) {
    return {
      locators: [
        {
          locatorStart: pos,
          locatorEnd: pos + searchTrimmed.length
        }
      ]
    };
  }
  return false;
});

export type SearchEventMatcher = (a: string) => SearchEventMatch;

export const createSearchEventMatcherBool = (a: string, options: { nullIsTrue?: boolean } = {}) => {
  return (c: string) => {
    return !!searcher({
      search: a,
      entity: c,
      ...options
    });
  };
};

export const createSearchEventMatcher = (a: string, options: { nullIsTrue?: boolean } = {}) => {
  return (c: string) => {
    return searcher({
      search: a,
      entity: c,
      ...options
    });
  };
};

export type SearchEventLocators = { locatorStart: number; locatorEnd: number }[];

export type SearchEventMatch =
  | {
      locators: SearchEventLocators;
    }
  | false;

export interface SearchEvent {
  search: string;
  matches: (input: string, options?: { nullIsTrue?: boolean }) => SearchEventMatch;
}

export interface SearchEngineInterface<T extends SearchResultEntry, S extends SearchEvent = SearchEvent> {
  doSearch(event: SearchEvent): SearchResult<T>;
}
